import React, { useContext, useMemo } from 'react';

import { useIsCurrentUserFleetOpsStaff } from 'components/Common/FleetOpsStaffOnly';
import AccountPickerContext from 'contexts/AccountPickerContext';
import MetricOptionsContext from 'contexts/MetricOptionsContext';
import useBonusProgramMetricAssignments from 'hooks/useBonusProgramMetricAssignments';

export type BonusMetricUsage = {
  programId: string;
  programName: string;
  // The metric is assigned to the program directly
  isDirect: boolean;
  // The metric is a part of these compound metrics assigned to the program
  viaCompoundMetricNames: string[];
};

interface BonusMetricLockContextType {
  usages: BonusMetricUsage[];
  isLoading: boolean;
  // In a bonus program and the current user is not FleetOps staff
  isLocked: boolean;
  // In a bonus program and the current user is FleetOps staff
  requiresStaffConfirmation: boolean;
  // What staff must type to confirm saving a change: the carrier name, as
  // staff work across many accounts
  confirmationText: string;
  // Why the save actions are disabled, if they are
  disabledReason?: string;
}

export const BonusMetricLockContext =
  React.createContext<BonusMetricLockContextType>({
    usages: [],
    isLoading: false,
    isLocked: false,
    requiresStaffConfirmation: false,
    confirmationText: '',
  });

export const describeBonusPrograms = (usages: BonusMetricUsage[]) => {
  if (usages.length === 1) {
    return `the ${usages[0].programName} bonus program`;
  }

  const names = usages.map((u) => u.programName);
  return `the ${names.slice(0, -1).join(', ')} and ${
    names[names.length - 1]
  } bonus programs`;
};

// describeBonusPrograms, with the program names in bold
export const BonusProgramsDescription = ({
  usages,
}: {
  usages: BonusMetricUsage[];
}) => (
  <>
    the{' '}
    {usages.map((usage, index) => (
      <React.Fragment key={usage.programId}>
        {index > 0 && (index === usages.length - 1 ? ' and ' : ', ')}
        <b>{usage.programName}</b>
      </React.Fragment>
    ))}{' '}
    {usages.length === 1 ? 'bonus program' : 'bonus programs'}
  </>
);

const BonusMetricLockProvider = ({
  metric,
  children,
}: {
  metric?: Metrics.NormalMetric | Metrics.CompoundMetric;
  children: JSX.Element | JSX.Element[];
}) => {
  const isFleetOpsStaff = !!useIsCurrentUserFleetOpsStaff();
  const { selectedAccount } = useContext(AccountPickerContext);
  const { metricOptionsLookup } = useContext(MetricOptionsContext);
  const { programAssignments, isLoading: isAssignmentsLoading } =
    useBonusProgramMetricAssignments();

  const metricId = metric ? metric.id : undefined;
  const usages = useMemo(() => {
    if (!metricId) {
      return [];
    }

    const newUsages: BonusMetricUsage[] = [];
    programAssignments.forEach(({ program, metrics }) => {
      const isDirect = metrics.some((a) => a.metricId === metricId);
      const viaCompoundMetricNames = metrics
        .filter(
          (a) =>
            a.metricId !== metricId &&
            (a.metricDateFields ?? []).some((p) => p.metricId === metricId),
        )
        .map((a) => {
          const compound = metricOptionsLookup[a.metricId];
          return compound ? compound.name : 'a compound metric';
        });

      if (isDirect || viaCompoundMetricNames.length > 0) {
        newUsages.push({
          programId: program.id,
          programName: program.name,
          isDirect,
          viaCompoundMetricNames: Array.from(new Set(viaCompoundMetricNames)),
        });
      }
    });

    return newUsages;
  }, [metricId, metricOptionsLookup, programAssignments]);

  const isLoading = !!metricId && isAssignmentsLoading;
  const isInBonus = usages.length > 0;
  const isLocked = isInBonus && !isFleetOpsStaff;
  const requiresStaffConfirmation = isInBonus && isFleetOpsStaff;

  const confirmationText = selectedAccount.accountName;

  const disabledReason = useMemo(() => {
    if (isLoading) {
      return 'Checking bonus program usage…';
    }
    if (isLocked) {
      return `Used in ${describeBonusPrograms(
        usages,
      )}. Only FleetOps staff can change it.`;
    }

    return undefined;
  }, [isLoading, isLocked, usages]);

  const value = useMemo(
    () => ({
      usages,
      isLoading,
      isLocked,
      requiresStaffConfirmation,
      confirmationText,
      disabledReason,
    }),
    [
      confirmationText,
      disabledReason,
      isLoading,
      isLocked,
      requiresStaffConfirmation,
      usages,
    ],
  );

  return (
    <BonusMetricLockContext.Provider value={value}>
      {children}
    </BonusMetricLockContext.Provider>
  );
};

export default BonusMetricLockProvider;
